import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { FEATURES } from '@yuan-math/config';
import { misconception } from '@yuan-math/exercises';
import {
  classOverview,
  conceptCohortView,
  studentsNeedingAttention,
  summariseStudent,
  type AttentionFlag,
  type ClassMember,
  type ClassRoster,
} from '@yuan-math/learning';
import { Badge, Button, Card, MasteryBar, SectionHeading, Stat } from '@yuan-math/ui';
import { registry } from '../state/LearnerContext.tsx';
import { useAuth } from '../state/AuthContext.tsx';
import { createClass, fetchClasses, fetchRoster, type TeacherClass } from '../state/classroom.ts';
import { AccountsUnavailable } from './RegisterPage.tsx';
import { Assignments } from '../components/Assignments.tsx';

/**
 * The teacher dashboard (§15).
 *
 * Built around one question — *who is stuck, and on what?* — because that is
 * what a teacher needs before a lesson, and it is the question the concept graph
 * is uniquely able to answer.
 *
 * A deliberate restraint: a teacher sees mastery, the concepts a student is
 * struggling with, and the misconceptions behind them. They do not see a log of
 * every answer. That would be surveillance rather than teaching, and the same
 * principle already governs the parent view (§14, §29).
 */
export function TeacherPage(): JSX.Element {
  const { account, signedIn, configured } = useAuth();

  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [members, setMembers] = useState<ClassMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    void fetchClasses(account.id).then((rows) => {
      if (cancelled) return;
      setClasses(rows);
      setSelectedId((current) => current ?? rows[0]?.id ?? null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [account]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    void fetchRoster(selectedId).then((rows) => {
      if (!cancelled) setMembers(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  if (!FEATURES.teacherDashboard) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">The teacher dashboard is not switched on</h1>
        <p className="page__lead">
          It is built and tested, but disabled by a feature flag while classroom management is finished.
        </p>
        <Link to="/" className="btn btn--secondary">Back to the home page</Link>
      </div>
    );
  }

  if (!configured) return <AccountsUnavailable />;

  if (!signedIn || !account) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">Sign in to see your classes</h1>
        <div className="button-row">
          <Link to="/signin" className="btn btn--primary">Sign in</Link>
        </div>
      </div>
    );
  }

  const selected = classes.find((entry) => entry.id === selectedId);

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">Your classes</h1>
        <p className="page__lead">
          Who is stuck, and on what — before the lesson rather than after it.
        </p>
      </header>

      {classes.length > 1 ? (
        <nav className="class-tabs" aria-label="Classes">
          {classes.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`class-tab ${entry.id === selectedId ? 'class-tab--current' : ''}`}
              onClick={() => setSelectedId(entry.id)}
            >
              {entry.name}
              <span className="class-tab__count">{entry.studentCount}</span>
            </button>
          ))}
        </nav>
      ) : null}

      {loading ? (
        <Card><p>Loading your classes…</p></Card>
      ) : !selected ? (
        <NewClassCard accountId={account.id} onCreated={(created) => {
          setClasses([created]);
          setSelectedId(created.id);
        }} />
      ) : (
        <ClassView roster={{ id: selected.id, name: selected.name, focusConceptIds: selected.focusConceptIds, members }} joinCode={selected.joinCode} />
      )}
    </div>
  );
}

function ClassView({ roster, joinCode }: { roster: ClassRoster; joinCode: string }): JSX.Element {
  const now = Date.now();
  const overview = useMemo(() => classOverview(roster, registry, now), [roster, now]);
  const flags = useMemo(() => studentsNeedingAttention(roster, registry, now, 8), [roster, now]);

  if (roster.members.length === 0) {
    return (
      <Card className="empty-class">
        <h2 className="parent-card__title">No students yet</h2>
        <p className="parent-card__note">
          Give your class this code to join. They enter it under Account → Join a class.
        </p>
        <p className="join-code">{joinCode}</p>
        <p className="page__note">
          Students join themselves — you cannot add someone who has not agreed. That is deliberate: consent to be
          observed has to come from the person being observed.
        </p>
      </Card>
    );
  }

  return (
    <>
      <div className="dashboard-stats dashboard-stats--wide">
        <Stat label="Students" value={overview.studentCount} />
        <Stat label="Active this week" value={`${overview.activeThisWeek} of ${overview.studentCount}`} />
        <Stat label="Average mastery" value={`${Math.round(overview.averageMastery * 100)}%`} hint="across taught concepts" />
        <Stat label="Need attention" value={flags.length} />
      </div>

      {overview.reteachConceptIds.length > 0 ? (
        <Card className="reteach-notice">
          <h2 className="consent-notice__title">Worth re-teaching</h2>
          <p>
            Enough of the class is stuck on{' '}
            <strong>{overview.reteachConceptIds.map((id) => registry.concept(id)?.title ?? id).join(', ')}</strong>{' '}
            that this is a teaching problem rather than an individual one. More practice will not fix it.
          </p>
          {overview.topMisconception ? (
            <p className="parent-card__note">
              The most widespread misunderstanding is{' '}
              <strong>{misconception(overview.topMisconception.id)?.title ?? overview.topMisconception.id}</strong>,
              affecting {overview.topMisconception.students} students.
            </p>
          ) : null}
        </Card>
      ) : null}

      <section className="dashboard-section">
        <SectionHeading
          title="Needs attention"
          description="Ordered by urgency. A missing prerequisite outranks the struggle it causes, because practising the harder concept cannot fill a hole underneath it."
        />
        {flags.length === 0 ? (
          <Card><p>Nobody is stuck. Worth saying out loud to them.</p></Card>
        ) : (
          <ul className="recommendation-list">
            {flags.map((flag, index) => (
              <li key={`${flag.profileId}-${flag.reason}-${index}`}>
                <AttentionCard flag={flag} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <Assignments classId={roster.id} members={roster.members} focusConceptIds={roster.focusConceptIds} />

      <section className="dashboard-section">
        <SectionHeading title="By concept" description="What landed, and what did not." />
        <div className="strand-grid">
          {roster.focusConceptIds.map((conceptId) => {
            const view = conceptCohortView(roster, registry, conceptId, now);
            return (
              <Card key={conceptId} className="strand-card">
                <div className="strand-card__head">
                  <h3>{view.title}</h3>
                  {view.needsReteaching ? <Badge tone="warn">Re-teach</Badge> : null}
                </div>
                <MasteryBar value={view.averageMastery} label="Class average" />
                <p className="strand-card__description">
                  {view.masteredCount} mastered · {view.strugglingCount} struggling · {view.notStartedCount} not started
                </p>
                {view.commonMisconceptions.length > 0 ? (
                  <ul className="misconception-mini">
                    {view.commonMisconceptions.map((entry) => (
                      <li key={entry.id}>
                        {misconception(entry.id)?.title ?? entry.id}
                        <span> — {entry.students} students</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Card>
            );
          })}
        </div>
      </section>

      <section className="dashboard-section">
        <SectionHeading title="Everyone" description="Mastery across the concepts you are teaching." />
        <div className="table-wrap">
          <table className="progress-table">
            <thead>
              <tr>
                <th scope="col">Student</th>
                <th scope="col">Mastery</th>
                <th scope="col">Mastered</th>
                <th scope="col">Struggling</th>
                <th scope="col">This week</th>
                <th scope="col">Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {roster.members
                .map((member) => summariseStudent(member, roster.focusConceptIds, now))
                .sort((a, b) => a.focusMastery - b.focusMastery)
                .map((summary) => (
                  <tr key={summary.profileId}>
                    <th scope="row">{summary.displayName}</th>
                    <td><MasteryBar value={summary.focusMastery} size="sm" /></td>
                    <td>{summary.masteredCount}</td>
                    <td>{summary.strugglingConceptIds.length || '—'}</td>
                    <td>{summary.attemptsThisWeek}</td>
                    <td>{summary.accuracy === undefined ? '—' : `${Math.round(summary.accuracy * 100)}%`}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <p className="page__note">
          Deliberately not shown: a log of every answer. You see where a student is stuck and why — not everything
          they have ever typed.
        </p>
      </section>

      <p className="dashboard-note">Join code for this class: <strong>{joinCode}</strong></p>
    </>
  );
}

function AttentionCard({ flag }: { flag: AttentionFlag }): JSX.Element {
  const labels: Record<AttentionFlag['reason'], string> = {
    'missing-foundation': 'Missing foundation',
    struggling: 'Needs teaching',
    inactive: 'Not practising',
    'ready-for-more': 'Ready for more',
  };

  return (
    <Card className="recommendation">
      <div className="recommendation__body">
        <span className={`recommendation__kind recommendation__kind--${flag.reason === 'ready-for-more' ? 'advance' : flag.reason === 'inactive' ? 'continue' : 'repair'}`}>
          {labels[flag.reason]}
        </span>
        <h3 className="recommendation__title">{flag.displayName}</h3>
        <p className="recommendation__reason">{flag.detail}</p>
      </div>
      {flag.conceptId ? (
        <div className="recommendation__actions">
          <Link to={`/concepts/${flag.conceptId}`} className="btn btn--secondary btn--sm">
            Open concept
          </Link>
        </div>
      ) : null}
    </Card>
  );
}

function NewClassCard({
  accountId,
  onCreated,
}: {
  accountId: string;
  onCreated(created: TeacherClass): void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  // Default to what is teachable now, so a new class is useful immediately.
  const suggested = registry.availableConcepts().slice(0, 6).map((concept) => concept.id);

  return (
    <Card className="new-class">
      <h2 className="parent-card__title">Create your first class</h2>
      <p className="parent-card__note">
        Name it, and you will get a code your students enter to join.
      </p>
      <form
        className="answer-form__row"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          void createClass(accountId, name.trim() || 'My class', suggested)
            .then((created) => {
              if (created) onCreated(created);
            })
            .finally(() => setBusy(false));
        }}
      >
        <input
          className="field__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. 6B Mathematics"
          maxLength={60}
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create class'}
        </Button>
      </form>
    </Card>
  );
}
